/**
 * Global Notes tab — internal agent notes attached to the conversation.
 *
 * Stored in a separate `conversation_notes` table, never part of the message
 * thread. Mirrors KC2 "Global Notes" right sidebar: the add-note form opens
 * in an ActionDrawer with Privacy toggle, rich-text Note, and Attachments.
 * The notes list is a clean timeline in the tab itself.
 */
import { useEffect, useRef, useState, type JSX } from 'react'
import { Eye, FileText, Globe, Lock, LockOpen, Loader2, MessageSquarePlus, Paperclip, Plus, X } from 'lucide-react'
import { cn } from '../../../utils/cn.js'
import { useConversationNotes } from '../../../hooks/useConversationNotes.js'
import { useUploadAttachment } from '../../../hooks/useUploadAttachment.js'
import { useToast } from '../../system/ToastProvider.js'
import { formatDateTime } from '../../../utils/timezone.js'
import RichTextEditor, { isEmptyHtml } from '../../common/RichTextEditor.js'
import ActionDrawer from './ActionDrawer.js'
import { Avatar, Field, GhostButton, PrimaryButton, R } from './primitives.js'
import { SafeHtml } from '../../common/SafeHtml.js'

export interface GlobalNotesTabProps {
  conversationId: string
  canEdit: boolean
  onAttachmentSelect?: ((attachmentId: string) => void) | undefined
}

const ACCEPTED_EXTENSIONS = '.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.gif,.png,.pdf,.txt,.odt,.ods,.zip,.rar,.mp4,.mov,.avi,.wmv,.flv,.mkv,.webm,.mpeg,.mpg,.m4v'

interface PendingAttachment {
  localId: string
  name: string
  state: 'uploading' | 'uploaded' | 'error'
  attachmentId?: string | undefined
  mimeType?: string | undefined
  previewUrl?: string | undefined
  file?: File | undefined
}

export default function GlobalNotesTab({
  conversationId,
  canEdit,
  onAttachmentSelect,
}: GlobalNotesTabProps): JSX.Element {
  const { notes, loading, creating, createNote } = useConversationNotes(conversationId)
  const { upload } = useUploadAttachment()
  const toast = useToast()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [isPrivate, setIsPrivate] = useState(true)
  const [pending, setPending] = useState<PendingAttachment[]>([])
  const [attachmentIds, setAttachmentIds] = useState<string[]>([])
  const fileRef = useRef<HTMLInputElement>(null)

  // Reset form when drawer opens
  useEffect(() => {
    if (drawerOpen) {
      setDraft('')
      setIsPrivate(true)
      setPending([])
      setAttachmentIds([])
    }
  }, [drawerOpen])

  const addFiles = (files: FileList | null): void => {
    if (!files || files.length === 0) return
    const list = Array.from(files)
    const additions: PendingAttachment[] = list.map((f) => ({
      localId: crypto.randomUUID(),
      name: f.name,
      state: 'uploading',
      mimeType: f.type || 'application/octet-stream',
      file: f,
    }))
    setPending((prev) => [...prev, ...additions])
    additions.forEach((a, i) => {
      const file = list[i]
      if (!file) return
      void (async () => {
        try {
          const res = await upload(file)
          const isImage = res.mimeType.startsWith('image/')
          setPending((prev) => prev.map((p) => p.localId === a.localId ? {
            ...p,
            state: 'uploaded',
            attachmentId: res.attachmentId,
            mimeType: res.mimeType,
            previewUrl: isImage ? `/api/attachments/${res.attachmentId}` : undefined,
          } : p))
          setAttachmentIds((prev) => [...prev, res.attachmentId])
        } catch (err) {
          console.error('[GlobalNotesTab] Upload FAILED', { localId: a.localId, name: a.name, error: (err as Error)?.message })
          setPending((prev) => prev.map((p) => p.localId === a.localId ? { ...p, state: 'error' } : p))
        }
      })()
    })
  }

  const removeAttachment = (localId: string): void => {
    setPending((prev) => {
      const removed = prev.find((p) => p.localId === localId)
      if (removed?.attachmentId) setAttachmentIds((ids) => ids.filter((id) => id !== removed.attachmentId))
      return prev.filter((p) => p.localId !== localId)
    })
  }

  const uploading = pending.some((p) => p.state === 'uploading')

  const submit = async (): Promise<void> => {
    if (isEmptyHtml(draft)) return
    const text = draft.replace(/<[^>]*>/g, '').trim()
    if (!text) return
    const note = await createNote({
      body: text,
      bodyHtml: draft,
      isPrivate,
      attachments: attachmentIds,
    })
    if (note) {
      toast.success('Note added')
      setDrawerOpen(false)
    } else {
      toast.error('Failed to add note')
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-slate-800">Global Notes</span>
          {notes.length > 0 && (
            <span className="text-xs font-medium text-slate-400">({notes.length})</span>
          )}
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className={cn(
              'flex items-center gap-1.5 bg-[#3366FF] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#2952CC]',
              R,
            )}
          >
            <Plus className="h-3.5 w-3.5" />
            Add Note
          </button>
        )}
      </div>

      {/* Notes list */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
          </div>
        ) : notes.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <div className={cn('flex h-16 w-16 items-center justify-center bg-blue-50 text-[#3366FF]/60', R)}>
              <MessageSquarePlus className="h-7 w-7" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-500">No global notes yet</p>
              <p className="mt-0.5 text-xs text-slate-400">
                Internal notes are visible to your team, not the customer.
              </p>
            </div>
            {canEdit && (
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className={cn(
                  'mt-2 flex items-center gap-1.5 bg-[#3366FF] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#2952CC]',
                  R,
                )}
              >
                <Plus className="h-3.5 w-3.5" />
                Add Note
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2 lg:grid-cols-3">
            {notes.map((note) => {
              const author = note.authorName ?? 'Unknown'
              return (
                <div
                  key={note.id}
                  className={cn(
                    'group flex flex-col overflow-hidden bg-white shadow-sm transition-all hover:shadow-md',
                    R,
                  )}
                >
                  {/* Top accent bar */}
                  <div className={cn('h-0.5 w-full', note.isPrivate ? 'bg-amber-400' : 'bg-emerald-400')} />

                  {/* Card header */}
                  <div className="flex items-center gap-2.5 px-4 pt-3">
                    <Avatar name={author} size={38} />
                    <div className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-slate-800">{author}</span>
                      <span className="text-[11px] text-slate-400">{formatDateTime(note.createdAt)}</span>
                    </div>
                    {note.isPrivate ? (
                      <Lock className="h-3.5 w-3.5 shrink-0 text-amber-400" />
                    ) : (
                      <Globe className="h-3.5 w-3.5 shrink-0 text-emerald-400" />
                    )}
                  </div>

                  {/* Card body */}
                  <div className="flex-1 px-4 py-4">
                    {note.bodyHtml ? (
                      <SafeHtml
                        className="line-clamp-5 text-sm leading-relaxed text-slate-600 [&_a]:text-[#3366FF] [&_a]:underline"
                        html={note.bodyHtml}
                      />
                    ) : (
                      <p className="line-clamp-5 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">{note.body}</p>
                    )}
                  </div>

                  {/* Attachments footer */}
                  {note.attachments && note.attachments.length > 0 && (
                    <div className="border-t border-slate-50 px-4 py-2.5">
                      <div className="mb-1.5 flex items-center gap-1.5">
                        <Paperclip className="h-3 w-3 shrink-0 text-slate-300" />
                        <span className="text-[11px] font-medium text-slate-400">
                          {note.attachments.length} {note.attachments.length === 1 ? 'attachment' : 'attachments'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {note.attachments.map((att) => {
                          if (typeof att === 'string') return null
                          const isImage = att.mimeType?.startsWith('image/') ?? false
                          return isImage ? (
                            <button
                              key={att.id}
                              type="button"
                              onClick={() => onAttachmentSelect?.(att.id)}
                              className="group relative"
                            >
                              <img
                                src={att.url}
                                alt={att.filename}
                                className="h-14 w-14 rounded-[3px] border border-slate-200 object-cover transition-all group-hover:border-[#3366FF]"
                              />
                              <span className="absolute inset-0 flex items-center justify-center rounded-[3px] bg-black/0 opacity-0 transition-all group-hover:bg-black/30 group-hover:opacity-100">
                                <Eye className="h-4 w-4 text-white" />
                              </span>
                            </button>
                          ) : (
                            <button
                              key={att.id}
                              type="button"
                              onClick={() => onAttachmentSelect?.(att.id)}
                              className="group inline-flex items-center gap-1.5 rounded-[3px] border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-600 transition-colors hover:border-[#3366FF] hover:bg-[#F5F7FF]"
                            >
                              <FileText className="h-3 w-3 shrink-0 text-slate-400 group-hover:text-[#3366FF]" />
                              <span className="max-w-[120px] truncate">{att.filename}</span>
                              <Eye className="h-3 w-3 shrink-0 text-slate-300 group-hover:text-[#3366FF]" />
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Add Note Drawer — mirrors KC2 Global Notes sidebar */}
      <ActionDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Global Notes"
        subtitle="Add an internal note"
        icon={<MessageSquarePlus className="h-4 w-4" />}
        width={560}
        footer={
          <>
            <GhostButton onClick={() => setDrawerOpen(false)}>Cancel</GhostButton>
            <PrimaryButton onClick={() => void submit()} disabled={isEmptyHtml(draft) || creating || uploading}>
              {creating ? 'Saving…' : 'Save'}
            </PrimaryButton>
          </>
        }
      >
        <div className="space-y-4">
          {/* Privacy */}
          <Field label="Privacy">
            <button
              type="button"
              onClick={() => setIsPrivate(!isPrivate)}
              className={cn(
                'flex items-center gap-1.5 border px-3 py-1.5 text-xs font-medium transition-colors',
                isPrivate
                  ? 'border-[#3366FF] bg-blue-50 text-[#3366FF]'
                  : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50',
                R,
              )}
            >
              {isPrivate ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
              {isPrivate ? 'Private' : 'Public'}
            </button>
          </Field>

          {/* Note (required) */}
          <Field label="Note" required>
            <RichTextEditor
              value={draft}
              onChange={setDraft}
              height={200}
              placeholder="Write an internal note…"
            />
          </Field>

          {/* Attachments */}
          <Field label="Attachments">
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault() }}
              onDrop={(e) => {
                e.preventDefault()
                
                addFiles(e.dataTransfer.files)
              }}
              className={cn(
                'cursor-pointer border-2 border-dashed border-slate-200 px-4 py-6 text-center text-xs text-slate-400 transition-colors hover:border-slate-300 hover:bg-slate-50',
                R,
              )}
            >
              <Paperclip className="mx-auto mb-2 h-5 w-5 text-slate-300" />
              Drag 'n' drop some files here, or click to select files.
              <div className="mt-1 text-[10px] text-slate-300">
                (Only {ACCEPTED_EXTENSIONS.replace(/\./g, '').replace(/,/g, ', ')} files will be accepted)
              </div>
            </div>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept={ACCEPTED_EXTENSIONS}
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
            {pending.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {pending.map((p) => (
                  <div
                    key={p.localId}
                    className={cn('flex items-center gap-2 border border-slate-100 px-3 py-2', R)}
                  >
                    {p.previewUrl ? (
                      <img src={p.previewUrl} alt={p.name} className="h-8 w-8 rounded object-cover" />
                    ) : (
                      <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                    )}
                    <span className="flex-1 truncate text-xs text-slate-600">{p.name}</span>
                    {p.state === 'uploading' && (
                      <span className="text-xs text-slate-400">Uploading…</span>
                    )}
                    {p.state === 'error' && (
                      <span className="text-xs text-red-500">Failed</span>
                    )}
                    <button
                      type="button"
                      onClick={() => removeAttachment(p.localId)}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Field>
        </div>
      </ActionDrawer>
    </div>
  )
}
